import * as THREE from "three";
import { CATALOG_TEXTURE_LIMITS } from "../catalog/catalogQueries";
import { trackFloorplan3DMetric } from "../metrics/floorplan3DMetrics";
import {
  getTextureImageSize,
  wallTextureRepeatXYFromScaleAndAspect,
} from "./wallTextureTiling";

type TextureCacheEntry = {
  texture: THREE.Texture;
  bytes: number;
  lastUsed: number;
};

const cache = new Map<string, TextureCacheEntry>();
/** In-flight loads so concurrent mesh mounts share one CORS fetch. */
const inflight = new Map<string, Promise<THREE.Texture>>();
let totalBytes = 0;
let clock = 0;

function isLowEndDevice(): boolean {
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number })
    .deviceMemory;
  return typeof deviceMemory === "number" && deviceMemory <= 4;
}

export function getTextureBudgetBytes(): number {
  return isLowEndDevice()
    ? CATALOG_TEXTURE_LIMITS.lowEndMaxTextureBytes
    : CATALOG_TEXTURE_LIMITS.maxTextureBytes;
}

export function getMaxCatalogTextureSize(): number {
  return isLowEndDevice()
    ? CATALOG_TEXTURE_LIMITS.lowEndMaxTextureSize
    : CATALOG_TEXTURE_LIMITS.maxTextureSize;
}

/**
 * Hard cap for wall / floor catalog albedos (VRAM + upload). Furniture and
 * other slots may still use {@link getMaxCatalogTextureSize}.
 */
export const WALL_FLOOR_CATALOG_TEXTURE_SIZE =
  CATALOG_TEXTURE_LIMITS.lowEndMaxTextureSize;

export function getResizedCatalogImageUrl(
  imageUrl: string,
  maxSize = getMaxCatalogTextureSize(),
  options?: { forceWidth?: boolean },
): string {
  try {
    const url = new URL(imageUrl, window.location.href);
    if (options?.forceWidth || !url.searchParams.has("w")) {
      url.searchParams.set("w", String(maxSize));
    }
    if (!url.searchParams.has("q")) url.searchParams.set("q", "82");
    return url.toString();
  } catch {
    return imageUrl;
  }
}

/** Persist / preview URL for wall and floor surfaces — always ≤ 512px. */
export function getWallFloorCatalogImageUrl(imageUrl: string): string {
  return getResizedCatalogImageUrl(imageUrl, WALL_FLOOR_CATALOG_TEXTURE_SIZE, {
    forceWidth: true,
  });
}

/**
 * Free-string angle on ProductAIImage for seamless Flooring/Wallpaper textures
 * baked in admin via bakeProductMaterialTexture.
 */
export const PRODUCT_MATERIAL_ANGLE = "MATERIAL";

type WallFloorProductImageSource = {
  images?: Array<string | null | undefined> | null;
  aiGeneratedImages?: Array<{
    angle?: string | null;
    imageUrl?: string | null;
  } | null> | null;
};

/**
 * True when admin baked a seamless MATERIAL texture for this Flooring/Wallpaper product.
 */
export function hasWallFloorProductMaterialBake(
  product: WallFloorProductImageSource,
): boolean {
  return (product.aiGeneratedImages ?? []).some(
    (img) =>
      Boolean(img?.imageUrl?.trim()) &&
      (img?.angle ?? "").trim().toLowerCase() ===
        PRODUCT_MATERIAL_ANGLE.toLowerCase(),
  );
}

/** Picker thumb size — only loaded when the user opens the texture image step. */
export const PRODUCT_TEXTURE_PICKER_THUMB_SIZE = 128;

export type WallFloorProductTextureImageOption = {
  /** Stable key for React lists. */
  id: string;
  /** Original catalog / AI URL (may be large). */
  rawUrl: string;
  /** Small CDN thumb for the picker UI. */
  thumbUrl: string;
  /** Wall/floor apply URL (≤512). */
  applyUrl: string;
  /** Catalog slot index when from images[], else null. */
  catalogIndex: number | null;
  /** AI angle when from aiGeneratedImages, else null. */
  aiAngle: string | null;
  /** True when this is the admin MATERIAL bake. */
  isMaterialBake: boolean;
};

type WallFloorProductTextureImageSource = WallFloorProductImageSource & {
  additionalImages?: Array<string | null | undefined> | null;
};

/**
 * Deduped texture choices for wallpaper/flooring: MATERIAL bake, other AI
 * angles, catalog images[], then additionalImages. Does not fetch — callers
 * only render thumbs when the picker is open.
 */
export function listWallFloorProductTextureImageOptions(
  product: WallFloorProductTextureImageSource,
): WallFloorProductTextureImageOption[] {
  const options: WallFloorProductTextureImageOption[] = [];
  const seen = new Set<string>();

  const push = (
    raw: string | null | undefined,
    meta: {
      id: string;
      catalogIndex: number | null;
      aiAngle: string | null;
      isMaterialBake: boolean;
    },
  ) => {
    const rawUrl = raw?.trim();
    if (!rawUrl || seen.has(rawUrl)) return;
    seen.add(rawUrl);
    options.push({
      id: meta.id,
      rawUrl,
      thumbUrl: getResizedCatalogImageUrl(
        rawUrl,
        PRODUCT_TEXTURE_PICKER_THUMB_SIZE,
        { forceWidth: true },
      ),
      applyUrl: getWallFloorCatalogImageUrl(rawUrl),
      catalogIndex: meta.catalogIndex,
      aiAngle: meta.aiAngle,
      isMaterialBake: meta.isMaterialBake,
    });
  };

  const aiImages = product.aiGeneratedImages ?? [];
  aiImages.forEach((img, index) => {
    const angle = (img?.angle ?? "").trim();
    const isMaterial =
      angle.toLowerCase() === PRODUCT_MATERIAL_ANGLE.toLowerCase();
    push(img?.imageUrl, {
      id: `ai-${index}-${angle || "image"}`,
      catalogIndex: null,
      aiAngle: angle || null,
      isMaterialBake: isMaterial,
    });
  });

  // Prefer MATERIAL first in the picker without rewriting the list twice:
  // re-sort so material bake leads, then other AI, then catalog.
  (product.images ?? []).forEach((url, index) => {
    push(url, {
      id: `catalog-${index}`,
      catalogIndex: index,
      aiAngle: null,
      isMaterialBake: false,
    });
  });

  (product.additionalImages ?? []).forEach((url, index) => {
    push(url, {
      id: `additional-${index}`,
      catalogIndex: null,
      aiAngle: null,
      isMaterialBake: false,
    });
  });

  options.sort((a, b) => {
    if (a.isMaterialBake !== b.isMaterialBake) {
      return a.isMaterialBake ? -1 : 1;
    }
    if (Boolean(a.aiAngle) !== Boolean(b.aiAngle)) {
      return a.aiAngle ? -1 : 1;
    }
    return 0;
  });

  return options;
}

/**
 * Raw (unresized) image URL for wall/floor catalog products:
 * prefer aiGeneratedImages with angle MATERIAL, else images[0].
 */
export function resolveWallFloorProductRawImageUrl(
  product: WallFloorProductImageSource,
): string | undefined {
  const material = (product.aiGeneratedImages ?? []).find(
    (img) =>
      img?.imageUrl &&
      (img.angle ?? "").trim().toLowerCase() ===
        PRODUCT_MATERIAL_ANGLE.toLowerCase(),
  );
  if (material?.imageUrl) return material.imageUrl;
  const fallback = product.images?.[0];
  return fallback?.trim() || undefined;
}

/**
 * Persist / preview URL for Flooring & Wallpaper catalog products —
 * MATERIAL bake when present, otherwise first catalog image, capped at 512px.
 */
export function getWallFloorProductTextureUrl(
  product: WallFloorProductImageSource,
): string | undefined {
  const raw = resolveWallFloorProductRawImageUrl(product);
  if (!raw) return undefined;
  return getWallFloorCatalogImageUrl(raw);
}

/**
 * Stable cache-key marker for WebGL / CORS image loads.
 *
 * Picker thumbnails use CSS background-image / plain <img> on the resized URL
 * (no CORS). If WebGL then loads that same URL with crossOrigin=anonymous, the
 * browser can reuse the opaque cached response and report a false CORS failure.
 * A fixed `fp3d=1` param keeps a separate, cacheable CORS entry — same idea as
 * the client’s canvas `?t=` / `?canvas=true` markers, without busting every load.
 *
 * Do not use the query key `cors`: S3 treats `?cors` as the bucket CORS
 * subresource and returns 405/403 instead of the object.
 */
export const CATALOG_TEXTURE_CACHE_PARAM = "fp3d";
export const CATALOG_TEXTURE_CACHE_VALUE = "1";

/**
 * URL used for anonymous CORS catalog texture fetches (and the in-memory
 * texture cache key). Distinct from {@link getResizedCatalogImageUrl} so UI
 * previews never poison WebGL.
 */
export function getCorsCatalogTextureUrl(
  imageUrl: string,
  maxSize = getMaxCatalogTextureSize(),
  options?: { forceWidth?: boolean },
): string {
  const resized = getResizedCatalogImageUrl(imageUrl, maxSize, options);
  try {
    const url = new URL(resized, window.location.href);
    url.searchParams.set(
      CATALOG_TEXTURE_CACHE_PARAM,
      CATALOG_TEXTURE_CACHE_VALUE,
    );
    return url.toString();
  } catch {
    return resized;
  }
}

/**
 * Load a catalog albedo the same way backdrop images do: an HTMLImageElement
 * with anonymous CORS, followed by a THREE.Texture from the decoded image.
 */
async function loadCorsCatalogImage(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () =>
      reject(new Error(`Failed to load catalog image: ${url}`));
    image.crossOrigin = "anonymous";
    image.src = url;
  });
  return image;
}

export async function loadCatalogTexture(
  imageUrl: string,
  renderer: THREE.WebGLRenderer,
  maxSize = getMaxCatalogTextureSize(),
  options?: { forceWidth?: boolean },
): Promise<THREE.Texture> {
  const key = getCorsCatalogTextureUrl(imageUrl, maxSize, options);
  const cached = cache.get(key);
  if (cached) {
    cached.lastUsed = ++clock;
    return cached.texture;
  }

  const pending = inflight.get(key);
  if (pending) return pending;

  const loadPromise = (async () => {
    const loadStart = performance.now();
    let image: HTMLImageElement;
    try {
      image = await loadCorsCatalogImage(key);
    } catch (error) {
      const reason =
        error instanceof Error
          ? error.message
          : error instanceof Event
            ? error.type
            : String(error);
      throw new Error(`Failed to load catalog texture (${reason}): ${key}`);
    }

    trackFloorplan3DMetric("texture_load", {
      url: key,
      duration: performance.now() - loadStart,
    });

    // Match BackdropImage: Texture from the decoded image, not TextureLoader.
    const texture = new THREE.Texture(image);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(
      CATALOG_TEXTURE_LIMITS.maxAnisotropy,
      renderer.capabilities.getMaxAnisotropy(),
    );
    texture.needsUpdate = true;

    const width = image.naturalWidth || maxSize;
    const height = image.naturalHeight || maxSize;
    const bytes = Math.max(width * height * 4 * 1.33, 1);

    cache.set(key, { texture, bytes, lastUsed: ++clock });
    totalBytes += bytes;
    evictUntilWithinBudget();
    return texture;
  })();

  inflight.set(key, loadPromise);
  try {
    return await loadPromise;
  } finally {
    inflight.delete(key);
  }
}

function evictUntilWithinBudget() {
  const budget = getTextureBudgetBytes();
  while (totalBytes > budget && cache.size > 0) {
    const oldest = Array.from(cache.entries()).sort(
      (a, b) => a[1].lastUsed - b[1].lastUsed,
    )[0];
    if (!oldest) return;
    cache.delete(oldest[0]);
    totalBytes -= oldest[1].bytes;
    oldest[1].texture.dispose();
  }
}

type TextureVariantEntry = {
  texture: THREE.Texture;
  refs: number;
};

/** Shared clones keyed by image + UV transform so stairs/meshes reuse one map. */
const variants = new Map<string, TextureVariantEntry>();

function variantCacheKey(
  imageUrl: string,
  repeatScale: number,
  repeatScaleY: number | undefined,
  rotation: number,
  uvChannel: number,
  maxSize: number,
  forceWidth: boolean,
): string {
  const ry =
    repeatScaleY === undefined ? "auto" : String(repeatScaleY);
  const corsUrl = getCorsCatalogTextureUrl(imageUrl, maxSize, {
    forceWidth: forceWidth || undefined,
  });
  return `${corsUrl}|r${repeatScale}|ry${ry}|rot${rotation}|uv${uvChannel}`;
}

/**
 * Acquire a shared Texture clone with per-material repeat/rotation.
 * Stairs mount many CatalogStandardMaterial instances; sharing clones avoids
 * N× step texture clones and cancelled-load races when switching materials.
 *
 * `uvChannel` selects which geometry UV set Three.js samples (0 = `uv`,
 * 1 = metre-space `uv1` on walls from `applyWallTextureUVs`).
 * Optional `repeatScaleY` sets vertical tiles-per-metre on walls; when omitted,
 * height follows the albedo aspect ratio.
 * `maxTextureSize` + `forceWidth` cap wall/floor albedos at 512 even when the
 * stored URL still has a larger `w`.
 */
export async function acquireCatalogTextureVariant(
  imageUrl: string,
  renderer: THREE.WebGLRenderer,
  options: {
    repeatScale: number;
    repeatScaleY?: number;
    rotation: number;
    uvChannel?: 0 | 1;
    maxTextureSize?: number;
    forceWidth?: boolean;
  },
): Promise<THREE.Texture> {
  const uvChannel = options.uvChannel ?? 0;
  const maxSize = options.maxTextureSize ?? getMaxCatalogTextureSize();
  const forceWidth = Boolean(options.forceWidth);
  const key = variantCacheKey(
    imageUrl,
    options.repeatScale,
    options.repeatScaleY,
    options.rotation,
    uvChannel,
    maxSize,
    forceWidth,
  );
  const existing = variants.get(key);
  if (existing) {
    existing.refs += 1;
    return existing.texture;
  }

  const base = await loadCatalogTexture(imageUrl, renderer, maxSize, {
    forceWidth: forceWidth || undefined,
  });
  const clone = base.clone();
  if (uvChannel === 1) {
    // Walls: horizontal tiles-per-metre; vertical from override or aspect.
    const size = getTextureImageSize(base);
    const { repeatX, repeatY } = size
      ? wallTextureRepeatXYFromScaleAndAspect(
          options.repeatScale,
          size.width,
          size.height,
          options.repeatScaleY,
        )
      : {
          repeatX: options.repeatScale,
          repeatY: options.repeatScaleY ?? options.repeatScale,
        };
    clone.repeat.set(repeatX, repeatY);
  } else {
    clone.repeat.set(options.repeatScale, options.repeatScale);
  }
  clone.rotation = options.rotation;
  clone.center.set(0.5, 0.5);
  clone.channel = uvChannel;
  clone.needsUpdate = true;
  variants.set(key, { texture: clone, refs: 1 });
  return clone;
}

/** Release a shared variant; disposes only when the last consumer unmounts. */
export function releaseCatalogTextureVariant(texture: THREE.Texture): void {
  for (const [key, entry] of Array.from(variants.entries())) {
    if (entry.texture !== texture) continue;
    entry.refs -= 1;
    if (entry.refs <= 0) {
      entry.texture.dispose();
      variants.delete(key);
    }
    return;
  }
}

export function clearCatalogTextureCache() {
  cache.forEach((entry) => entry.texture.dispose());
  cache.clear();
  inflight.clear();
  variants.forEach((entry) => entry.texture.dispose());
  variants.clear();
  totalBytes = 0;
}
